import React, { useState } from 'react';
import { IconX, IconCheck, IconChevronRight, IconLoader } from '../ui/Icons';
import PayPalCheckout from './PayPalCheckout';

const TIERS = [
    {
        id: 'SILVER',
        emoji: '🥈',
        name: 'Silber Partner',
        price: '99',
        period: '/ Monat',
        color: 'from-slate-400 to-slate-500',
        border: 'border-slate-300 dark:border-slate-500',
        badge: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
        features: [
            'Logo in der Partner-Sidebar',
            'Bis zu 5 aktive Anzeigen',
            'Direkte Bewerbungen über Plantlix',
            'Basis Talent-Pool Zugriff',
            'Monatsrechnung per E-Mail',
        ],
        notIncluded: ['Automatisch Featured-Anzeigen', 'Premium Platzierung auf Dashboard', 'KI-Matching mit Talent-Pool'],
        paypalPlanId: 'P-SILVER-DUMMY', // User needs to replace
    },
    {
        id: 'GOLD',
        emoji: '🥇',
        name: 'Gold Partner',
        price: '199',
        period: '/ Monat',
        color: 'from-amber-400 to-yellow-500',
        border: 'border-yellow-400 dark:border-yellow-500',
        badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
        popular: true,
        features: [
            'Logo groß auf der Karriere-Seite',
            'Unbegrenzte Anzeigen',
            'Alle Anzeigen automatisch "Featured"',
            'KI-Matching mit gesamtem Talent-Pool',
            'Premium Platzierung auf App-Dashboard',
            'Employer Branding Galerie & Video',
            'Prioritäts-Support',
        ],
        notIncluded: [],
        paypalPlanId: 'P-GOLD-DUMMY', // User needs to replace
    },
];

export default function PricingModal({ onClose, onSelectTier, currentLevel = 'BASIC', isLoading = false }) {
    const [selected, setSelected] = useState(null);

    const handleSelect = (tierId) => {
        setSelected(tierId);
        onSelectTier(tierId);
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="relative bg-white dark:bg-gray-950 w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl border border-gray-100 dark:border-white/10"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative p-8 pb-0">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                        <IconX className="w-5 h-5 text-gray-500" />
                    </button>

                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded-full text-xs font-black uppercase tracking-widest mb-4">
                            ✨ Plantlix Karriere Premium
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2">
                            Heb dich aus der Masse hervor
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                            Erreiche hunderte Azubis und Fachkräfte in deiner Region. Wähle dein Paket und starte sofort.
                        </p>
                        {currentLevel !== 'BASIC' && (
                            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-plantlix-100 dark:bg-plantlix-500/20 text-plantlix-700 dark:text-plantlix-300 rounded-full text-sm font-bold">
                                ✅ Aktuell: {currentLevel === 'SILVER' ? 'Silber Partner' : 'Gold Partner'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="px-8 pb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {TIERS.map(tier => {
                        const isCurrent = tier.id === currentLevel;
                        const isUpgrade = (currentLevel === 'SILVER' && tier.id === 'GOLD') || currentLevel === 'BASIC';
                        const isDowngrade = currentLevel === 'GOLD' && tier.id === 'SILVER';

                        return (
                            <div
                                key={tier.id}
                                className={`relative rounded-2xl border-2 p-6 transition-all ${tier.border} ${
                                    tier.popular
                                        ? 'ring-2 ring-yellow-400/50 shadow-xl shadow-yellow-500/10'
                                        : 'shadow-sm'
                                } ${isCurrent ? 'opacity-60' : ''}`}
                            >
                                {tier.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full text-[10px] font-black text-white uppercase tracking-widest shadow-md whitespace-nowrap">
                                        ⭐ Empfohlen
                                    </div>
                                )}
                                {isCurrent && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-plantlix-500 rounded-full text-[10px] font-black text-white uppercase tracking-widest shadow-md whitespace-nowrap">
                                        Dein Plan
                                    </div>
                                )}

                                {/* Tier Header */}
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`text-3xl`}>{tier.emoji}</div>
                                    <div>
                                        <h3 className="font-black text-gray-900 dark:text-white text-lg">{tier.name}</h3>
                                        <span className={`text-xs font-black uppercase px-2 py-0.5 rounded-full ${tier.badge}`}>
                                            {tier.id}
                                        </span>
                                    </div>
                                </div>

                                {/* Price */}
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-black text-gray-900 dark:text-white">{tier.price}€</span>
                                        <span className="text-gray-400 font-bold">{tier.period}</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">zzgl. MwSt. · monatlich kündbar</p>
                                </div>

                                {/* Features */}
                                <ul className="space-y-2.5 mb-6">
                                    {tier.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm">
                                            <div className="w-5 h-5 rounded-full bg-plantlix-100 dark:bg-plantlix-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <IconCheck className="w-3 h-3 text-plantlix-600 dark:text-plantlix-400" />
                                            </div>
                                            <span className="text-gray-700 dark:text-gray-300 font-medium">{f}</span>
                                        </li>
                                    ))}
                                    {tier.notIncluded.map((f, i) => (
                                        <li key={`no-${i}`} className="flex items-start gap-2.5 text-sm">
                                            <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <IconX className="w-3 h-3 text-gray-400" />
                                            </div>
                                            <span className="text-gray-400 font-medium">{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* PayPal Button or Indicator */}
                                {selected === tier.id ? (
                                    <div className="mt-4">
                                        <PayPalCheckout 
                                            type="SUBSCRIPTION"
                                            planId={tier.paypalPlanId}
                                            level={tier.id}
                                            onSuccess={() => {
                                                onClose();
                                                if (onSelectTier) onSelectTier(tier.id);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => !isCurrent && !isDowngrade && setSelected(tier.id)}
                                        disabled={isCurrent || isDowngrade || isLoading}
                                        className={`w-full py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all ${
                                            isCurrent
                                                ? 'bg-gray-100 dark:bg-white/5 text-gray-400 cursor-not-allowed'
                                                : isDowngrade
                                                ? 'bg-gray-100 dark:bg-white/5 text-gray-400 cursor-not-allowed'
                                                : tier.popular
                                                ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02] active:scale-95'
                                                : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:scale-[1.02] active:scale-95'
                                        }`}
                                    >
                                        {isCurrent ? (
                                            'Aktueller Plan'
                                        ) : isDowngrade ? (
                                            'Kein Downgrade möglich'
                                        ) : isUpgrade ? (
                                            <>
                                                Jetzt upgraden
                                                <IconChevronRight className="w-4 h-4" />
                                            </>
                                        ) : (
                                            <>
                                                Auswählen
                                                <IconChevronRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer: Featured Job Upsell */}
                <div className="mx-8 mb-8 p-5 bg-gradient-to-r from-plantlix-50 to-emerald-50 dark:from-plantlix-500/10 dark:to-emerald-500/10 rounded-2xl border border-plantlix-200/50 dark:border-plantlix-500/20">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="text-2xl">⭐</div>
                            <div>
                                <p className="font-black text-gray-900 dark:text-white text-sm">Einzelne Anzeige hervorheben</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                    49€ einmalig · 30 Tage Featured-Placement · sofortige Aktivierung
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic max-w-xs">
                            Nutzbar für einzelne Anzeigen direkt aus der Job-Verwaltung.
                        </p>
                    </div>
                </div>

                {/* Trust footer */}
                <div className="px-8 pb-6 text-center">
                    <p className="text-[11px] text-gray-400 dark:text-gray-600">
                        🔒 Sichere Zahlung via PayPal · Monatlich kündbar · MwSt. wird bei Zahlung berechnet
                    </p>
                </div>
            </div>
        </div>
    );
}
